/**
 * ReviewItem
 *
 * Single editable row for the review surface. Shows label, input, and
 * affordances to save/cancel or mark as reviewed. Emits hover events to
 * coordinate with the evidence pane.
 */
import { Check, X } from 'lucide-react';
import React from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/** Props for the `ReviewItem` editable row. */
interface ReviewItemProps {
  /** Unique id used for input and accessibility. */
  id: string;
  /** Field label shown to the user. */
  label: string;
  /** Current input value. */
  value: string;
  /** Whether this field must be filled or confirmed. */
  required?: boolean;
  /** Whether the field has already been marked reviewed. */
  isReviewed?: boolean;
  /** Whether to render the active highlight styling. */
  active?: boolean;
  /** Evidence hover key for coordinating with the evidence pane. */
  hoverKey?: string;
  /** Sets the current hover target in the parent. */
  onHover?: (key: string | null) => void;
  /** Change handler for the input value. */
  onChange?: (next: string) => void;
  /** Mark the field as reviewed. */
  onAccept?: () => void;
  /** Optional save handler (parent-provided). */
  onSave?: () => void;
  /** Optional cancel handler (when using explicit save UX). */
  onCancel?: () => void;
  /** Custom placeholder text; defaults to `Enter {label}`. */
  placeholder?: string;
}

export const ReviewItem: React.FC<ReviewItemProps> = ({
  id,
  label,
  value,
  required,
  isReviewed,
  active,
  hoverKey,
  onHover,
  onChange,
  onAccept,
  onSave,
  onCancel,
  placeholder,
}) => {
  const handleMouseEnter = () => onHover?.(hoverKey || id);

  return (
    <div
      role="group"
      aria-label={`${label} ${required ? '(required)' : '(optional)'}`}
      onMouseEnter={handleMouseEnter}
      onFocus={handleMouseEnter}
      className={`px-3 py-2 transition-colors ${
        active
          ? 'bg-indigo-50/40 ring-2 ring-indigo-200'
          : 'hover:bg-indigo-50/30'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {label}
          </span>
          {!required && (
            <span className="text-[10px] uppercase tracking-wide text-gray-500">
              Optional
            </span>
          )}
        </div>
        {isReviewed && (
          <div className="flex items-center gap-1 text-xs text-gray-500">
            <Check className="h-3.5 w-3.5 text-green-600" />
            <span>Reviewed</span>
          </div>
        )}
      </div>
      <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:gap-2">
        <div className="flex-1 min-w-0">
          <Input
            id={id}
            value={value}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={placeholder || `Enter ${label}`}
            aria-label={`${label} input`}
            className="h-9"
          />
        </div>
        <div className="mt-2 sm:mt-0 sm:w-48 flex items-center justify-end gap-2">
          {onCancel && (
            <Button
              size="sm"
              variant="ghost"
              className="text-xs"
              onClick={onCancel}
              aria-label={`Cancel ${label} edit`}
            >
              <X className="h-4 w-4 mr-1" /> Cancel
            </Button>
          )}
          {onSave && (
            <Button
              size="sm"
              className="text-xs"
              onClick={onSave}
              aria-label={`Save ${label}`}
            >
              Save
            </Button>
          )}
          {onAccept && !isReviewed && (
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={onAccept}
              aria-label={`Mark ${label} reviewed`}
            >
              <Check className="h-4 w-4 mr-1" /> Mark reviewed
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
